import { useTranslation } from 'react-i18next';
import { Button, Menu, MenuButton, MenuList, MenuItem, HStack, Text, Icon } from '@chakra-ui/react';
import { FaGlobe, FaChevronDown } from 'react-icons/fa';
import { useState, useEffect } from 'react';
import { api } from '../../services/api';

interface Language {
  id: string;
  name: string;
  native_name: string;
  code: string;
  is_default: boolean;
  is_active: boolean;
  sort_order: number;
}

export const LanguageSwitcher = () => {
  const { i18n } = useTranslation();
  const [languages, setLanguages] = useState<Language[]>([]);
  const [currentLanguage, setCurrentLanguage] = useState<string>('cs');
  const [loading, setLoading] = useState(true);

  // Fetch available languages from API
  useEffect(() => {
    const fetchLanguages = async () => {
      try {
        const response = await api.get('/i18n/languages');
        const activeLanguages = response.data.languages.filter((lang: Language) => lang.is_active);
        
        if (activeLanguages.length > 0) {
          setLanguages(activeLanguages);
        } else {
          // Fallback to default languages if API returns empty
          setLanguages([
            { id: 'cs', name: 'Czech', native_name: 'Čeština', code: 'cs', is_default: true, is_active: true, sort_order: 1 },
            { id: 'en', name: 'English', native_name: 'English', code: 'en', is_default: false, is_active: true, sort_order: 2 },
          ]);
        }
        
        // Set current language from i18n or default to Czech
        const current = i18n.language || 'cs';
        setCurrentLanguage(current);
      } catch (error) {
        // Fallback to default languages on error
        setLanguages([
          { id: 'cs', name: 'Czech', native_name: 'Čeština', code: 'cs', is_default: true, is_active: true, sort_order: 1 },
          { id: 'en', name: 'English', native_name: 'English', code: 'en', is_default: false, is_active: true, sort_order: 2 },
        ]);
        const current = i18n.language || 'cs';
        setCurrentLanguage(current);
      } finally {
        setLoading(false);
      }
    };

    fetchLanguages();
  }, [i18n.language]);

  // Change language
  const changeLanguage = async (languageCode: string) => {
    try {
      // Change language in i18next
      await i18n.changeLanguage(languageCode);
      
      // Save preference to backend if user is authenticated
      const token = localStorage.getItem('token');
      if (token) {
        try {
          await api.post('/i18n/user-language', { language_code: languageCode });
        } catch (error) {
          console.warn('Failed to save language preference to backend:', error);
        }
      }
      
      // Save to cookie
      document.cookie = `lang=${languageCode}; max-age=${365 * 24 * 60 * 60}; path=/`;
      
      setCurrentLanguage(languageCode);
      
      // Refresh page to update navigation and all content
      window.location.reload();
    } catch (error) {
      console.error('Failed to change language:', error);
    }
  };

  // Get current language display name
  const getCurrentLanguageDisplay = () => {
    const lang = languages.find(l => l.code === currentLanguage);
    return lang ? lang.native_name : 'Čeština';
  };

  if (loading || languages.length < 2) {
    return null; // Don't show if less than 2 languages or still loading
  }

  return (
    <Menu>
      <MenuButton
        as={Button}
        variant="ghost"
        size="sm"
        minWidth="auto"
        px={2}
        py={1}
        borderRadius="md"
        _hover={{ bg: "gray.100" }}
        _active={{ bg: "gray.200" }}
        fontSize="sm"
        fontWeight="medium"
      >
        <HStack spacing={1}>
          <Icon as={FaGlobe} fontSize="sm" />
          <Text fontSize="sm" fontWeight="medium" textTransform="uppercase">
            {currentLanguage === 'cs' ? 'CZ' : 'EN'}
          </Text>
          <Icon as={FaChevronDown} fontSize="xs" />
        </HStack>
      </MenuButton>
      <MenuList minWidth="100px" p={1} boxShadow="lg" border="1px" borderColor="gray.200">
        {languages
          .sort((a, b) => a.sort_order - b.sort_order)
          .map((language) => (
            <MenuItem
              key={language.code}
              onClick={() => changeLanguage(language.code)}
              isDisabled={language.code === currentLanguage}
              fontSize="sm"
              borderRadius="md"
              px={2}
              py={1}
              _hover={{ bg: "gray.100" }}
              _selected={{ bg: "blue.50", color: "blue.600" }}
            >
              <HStack spacing={1} width="100%" justifyContent="space-between">
                <Text fontSize="sm" fontWeight="medium" textTransform="uppercase">
                  {language.code === 'cs' ? 'CZ' : 'EN'}
                </Text>
                {language.code === currentLanguage && (
                  <Text fontSize="sm" color="blue.500" fontWeight="bold">
                    ✓
                  </Text>
                )}
              </HStack>
            </MenuItem>
          ))}
      </MenuList>
    </Menu>
  );
};
